<template>
  <div class="video-list-container">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>视频数据列表</span>
        <el-button style="float: right; padding: 3px 0" type="text" @click="refreshData">刷新</el-button>
      </div>

      <el-table
        :data="videoList"
        v-loading="loading"
        border
        style="width: 100%">
        <el-table-column
          align="center"
          header-align="center"
          label="序号"
          type="index"
          width="60"
          :index="indexMethod">
        </el-table-column>
        <el-table-column
          align="center"
          header-align="center"
          prop="id"
          label="ID"
          width="80">
        </el-table-column>
        <el-table-column
          prop="name"
          label="视频名称"
          min-width="260">
        </el-table-column>
        <el-table-column
          prop="size"
          label="文件大小"
          width="130">
          <template slot-scope="scope">
            {{ formatFileSize(scope.row.size) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="type"
          label="文件类型"
          width="140">
        </el-table-column>
        <el-table-column
          prop="uploadTime"
          label="上传时间"
          width="180">
        </el-table-column>
        <el-table-column
          label="操作"
          width="240"
          fixed="right">
          <template slot-scope="scope">
            <el-button
              size="mini"
              type="primary"
              @click="handlePlay(scope.row)">
              播放
            </el-button>
            <el-button
              size="mini"
              @click="copyLink(scope.row.url)">
              复制链接
            </el-button>
            <el-button
              size="mini"
              type="danger"
              @click="handleDelete(scope.row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        style="margin-top: 20px; text-align: right;">
      </el-pagination>
    </el-card>

    <el-dialog
      title="视频播放"
      :visible.sync="playerVisible"
      width="860px"
      @closed="resetPlayer">
      <div v-if="currentVideo" class="player-wrapper">
        <div class="player-meta">
          <div class="video-name">{{ currentVideo.name }}</div>
          <div class="video-info">
            <span>{{ formatFileSize(currentVideo.size) }}</span>
            <span>{{ currentVideo.uploadTime }}</span>
          </div>
        </div>
        <video
          ref="player"
          class="video-player"
          :src="currentVideo.url"
          controls
          playsinline
          preload="metadata">
          当前浏览器不支持视频播放。
        </video>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { fetchVideoList, deleteUploadFile } from '@/api/modules/upload'

export default {
  name: 'VideoList',
  data() {
    return {
      videoList: [],
      loading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      playerVisible: false,
      currentVideo: null
    }
  },
  created() {
    this.fetchVideoData()
  },
  methods: {
    async fetchVideoData() {
      this.loading = true

      try {
        const data = await fetchVideoList()
        const allVideos = data.list || []
        this.total = allVideos.length

        const startIndex = (this.currentPage - 1) * this.pageSize
        const endIndex = startIndex + this.pageSize
        this.videoList = allVideos.slice(startIndex, endIndex)
      } catch (error) {
        console.error('Failed to fetch video data:', error)
        this.videoList = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    formatFileSize(size) {
      if (size < 1024) {
        return `${size} B`
      }

      if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(2)} KB`
      }

      if (size < 1024 * 1024 * 1024) {
        return `${(size / (1024 * 1024)).toFixed(2)} MB`
      }

      return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`
    },
    handlePlay(row) {
      this.currentVideo = row
      this.playerVisible = true
      this.$nextTick(() => {
        const player = this.$refs.player
        if (player && player.load) {
          player.load()
        }
      })
    },
    resetPlayer() {
      const player = this.$refs.player
      if (player) {
        player.pause()
      }
      this.currentVideo = null
    },
    async copyLink(url) {
      try {
        await navigator.clipboard.writeText(url)
        this.$toast.success('视频链接已复制')
      } catch (error) {
        this.$toast.error('复制链接失败')
      }
    },
    async handleDelete(row) {
      try {
        await this.$confirm(`确定要删除视频 "${row.name}" 吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        await deleteUploadFile(row.name)

        if (this.currentVideo && this.currentVideo.name === row.name) {
          this.playerVisible = false
          this.resetPlayer()
        }

        this.$toast.success('删除成功')

        if (this.videoList.length === 1 && this.currentPage > 1) {
          this.currentPage -= 1
        }

        this.fetchVideoData()
      } catch (error) {
        if (error === 'cancel') {
          return
        }

        console.error('Failed to delete video:', error)
      }
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.fetchVideoData()
    },
    handleCurrentChange(page) {
      this.currentPage = page
      this.fetchVideoData()
    },
    refreshData() {
      this.fetchVideoData()
    },
    indexMethod(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1
    }
  }
}
</script>

<style scoped>
.video-list-container {
  /* 撑满内容区，滚动下沉到模块内部 */
  height: 100%;
  box-sizing: border-box;
  overflow: auto;
  padding: 0;
}

.clearfix:before,
.clearfix:after {
  display: table;
  content: "";
}

.clearfix:after {
  clear: both;
}

.player-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.player-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.video-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.video-info {
  font-size: 13px;
  color: #909399;
  display: flex;
  gap: 16px;
}

.video-player {
  width: 100%;
  max-height: 520px;
  background: #000;
  border-radius: 8px;
  outline: none;
}
</style>
